"use client";

import type { DiagnosticsEvent } from "@/lib/diagnostics/types";
import { useEffect, useSyncExternalStore } from "react";

const MAX_EVENTS = 200;
const DEBUG_STORE_GLOBAL_KEY = "__dwDiagnosticsStore";
// Dev-only pump that drains server-recorded diagnostics into the client store.
// Not latency-sensitive — a slow cadence is fine and keeps dev network noise low.
const SERVER_POLL_INTERVAL_MS = 10_000;

/**
 * `serverCursorMs` is the `since` sent on the next server poll: one ms past the
 * newest server event already merged, or 0 before the first poll. It lives on
 * the global store, not in the polling component, so closing and reopening the
 * diagnostics panel resumes from the cursor instead of re-reading the file.
 */
type DiagnosticsStoreGlobal = { events: DiagnosticsEvent[]; listeners: Set<() => void>; serverCursorMs: number; };

function getGlobalStore(): DiagnosticsStoreGlobal {
    const root = globalThis as typeof globalThis & { [DEBUG_STORE_GLOBAL_KEY]?: DiagnosticsStoreGlobal; };
    if (!root[DEBUG_STORE_GLOBAL_KEY]) {
        root[DEBUG_STORE_GLOBAL_KEY] = { events: [], listeners: new Set(), serverCursorMs: 0 };
    }
    return root[DEBUG_STORE_GLOBAL_KEY];
}

function emit(store: DiagnosticsStoreGlobal) {
    for (const listener of store.listeners) {
        listener();
    }
}

export function pushDiagnosticsEvent(event: DiagnosticsEvent) {
    const store = getGlobalStore();
    store.events = [event, ...store.events].slice(0, MAX_EVENTS);
    emit(store);
}

function mergeDiagnosticsEvents(incoming: DiagnosticsEvent[]) {
    if (incoming.length === 0) {
        return;
    }
    const store = getGlobalStore();
    const seen = new Set(store.events.map((event) => event.id));
    const merged = [...incoming.filter((event) => !seen.has(event.id)), ...store.events];
    merged.sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
    store.events = merged.slice(0, MAX_EVENTS);
    emit(store);
}

export function clearDiagnosticsEvents() {
    const store = getGlobalStore();
    store.events = [];
    emit(store);
}

function getDiagnosticsSnapshot(): DiagnosticsEvent[] {
    return getGlobalStore().events;
}

function subscribe(listener: () => void) {
    const store = getGlobalStore();
    store.listeners.add(listener);
    return () => {
        store.listeners.delete(listener);
    };
}

export function useDiagnosticsSnapshot(): DiagnosticsEvent[] {
    return useSyncExternalStore(subscribe, getDiagnosticsSnapshot, () => []);
}

async function pollServerDiagnostics() {
    const store = getGlobalStore();
    const url = store.serverCursorMs > 0 ? `/api/dev/diagnostics?since=${store.serverCursorMs}` : "/api/dev/diagnostics";
    try {
        const response = await fetch(url);
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as { data?: { events?: DiagnosticsEvent[] } };
        const events = payload.data?.events ?? [];
        if (events.length === 0) {
            return;
        }
        mergeDiagnosticsEvents(events);
        const newestMs = Math.max(...events.map((event) => Date.parse(event.ts)).filter(Number.isFinite));
        if (Number.isFinite(newestMs)) {
            // The server filters events with `ts >= since` (inclusive). Parking the
            // cursor on the newest ms re-delivers those same events on every poll
            // forever. Advance one ms past it. All events sharing `newestMs` are
            // already in this batch (the `>=` filter returned every ts === newestMs),
            // so the only theoretical loss is an event written at newestMs *after*
            // this read — negligible for dev diagnostics.
            store.serverCursorMs = newestMs + 1;
        }
    } catch {
        // Dev-only sink.
    }
}

/**
 * Polls `/api/dev/diagnostics` while the calling component is mounted.
 *
 * The only caller is `ConvexDebugSidePanel`, which mounts only while the
 * diagnostics panel is toggled open, so nothing polls while it is closed.
 * No-op outside development.
 */
export function useServerDiagnosticsPoll() {
    useEffect(() => {
        if (process.env.NODE_ENV !== "development") {
            return;
        }

        // Fast Refresh / repeated mounts can leave the previous setInterval alive
        // (effect cleanup does not always fire on HMR), so intervals accumulate.
        // Track the id on `window` and clear any prior poller before starting a
        // new one — only one ever runs per tab.
        const pollWindow = window as Window & { __dwDiagnosticsPollId?: number; };
        if (pollWindow.__dwDiagnosticsPollId !== undefined) {
            window.clearInterval(pollWindow.__dwDiagnosticsPollId);
        }

        void pollServerDiagnostics();
        const interval = window.setInterval(() => {
            void pollServerDiagnostics();
        }, SERVER_POLL_INTERVAL_MS);
        pollWindow.__dwDiagnosticsPollId = interval;

        return () => {
            window.clearInterval(interval);
            if (pollWindow.__dwDiagnosticsPollId === interval) {
                pollWindow.__dwDiagnosticsPollId = undefined;
            }
        };
    }, []);
}
